import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axiosInstance from '../api/axiosInstance';

export type UserRole = 'super_admin' | 'pharmacy_admin' | 'pharmacy_staff';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  title?: string;
  pharmacyId?: string;
  pharmacyName?: string;
  permissions: string[];
  lastLogin: string;
  isActive: boolean;
}

export interface Pharmacy {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  licenseNumber: string;
  isActive: boolean;
  totalOrders: number;
  activeOrders: number;
  joinedDate: string;
}

interface UserContextType {
  user: User | null;
  pharmacies: Pharmacy[];
  setUser: (user: User | null) => void;
  setPharmacies: (pharmacies: Pharmacy[]) => void;
  hasPermission: (permission: string) => boolean;
  isSuperAdmin: () => boolean;
  isPharmacyUser: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);

  const hasPermission = (permission: string): boolean => {
    return user?.permissions.includes(permission) || false;
  };

  const isSuperAdmin = (): boolean => {
    return user?.role === 'super_admin';
  };

  const isPharmacyUser = (): boolean => {
    return user?.role === 'pharmacy_admin' || user?.role === 'pharmacy_staff';
  };

  // Fetch pharmacies from API only once
  useEffect(() => {
    const fetchPharmacies = async () => {
      try {
        const response = await axiosInstance.get('/pharmacy/all');
        console.log('UserContext: Pharmacy API response:', response.data);
        if (response.data?.data) {
          setPharmacies(response.data.data);
        } else if (Array.isArray(response.data)) {
          // Handle case where data is directly an array
          setPharmacies(response.data);
        }
      } catch (error) {
        console.error('Error fetching pharmacies:', error);
        // Keep empty array as fallback
        setPharmacies([]);
      }
    };

    // Only fetch if we don't have pharmacies data yet
    if (pharmacies.length === 0) {
      fetchPharmacies();
    }
  }, [pharmacies.length]);

  return (
    <UserContext.Provider
      value={{
        user,
        pharmacies,
        setUser,
        setPharmacies,
        hasPermission,
        isSuperAdmin,
        isPharmacyUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}